import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import CloseLine from '@/assets/icons/close_line.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import styles from './Alert.module.scss';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

/** How a screen reader announces the alert when it appears. */
export type AlertLive = 'assertive' | 'polite' | 'off';

export interface AlertProps {
    tone: AlertTone;
    children?: ReactNode;
    /** `inline` is a bordered box in the content; `banner` is a full-width strip. */
    layout?: 'inline' | 'banner';
    size?: 'small' | 'medium';
    align?: 'start' | 'center';
    title?: ReactNode;
    /** Decorative; it takes the tone's colour through `currentColor`. */
    icon?: ReactNode;
    /** Content-sized slot after the message, such as a button. */
    action?: ReactNode;
    /** Shows a close button that calls this. */
    onDismiss?: () => void;
    dismissLabel?: string;
    /**
     * Defaults follow the tone: problems interrupt (`role="alert"`), information waits
     * (`role="status"`), and the neutral strip is not announced. Pass `off` when the
     * content carries its own live region.
     */
    live?: AlertLive;
    className?: string;
}

const DEFAULT_LIVE: Record<AlertTone, AlertLive> = {
    danger: 'assertive',
    warning: 'assertive',
    info: 'polite',
    success: 'polite',
    neutral: 'off',
};

const ROLE: Record<AlertLive, 'alert' | 'status' | undefined> = {
    assertive: 'alert',
    polite: 'status',
    off: undefined,
};

export function Alert(props: AlertProps) {
    const {
        tone,
        children,
        layout = 'inline',
        size = 'medium',
        align = 'start',
        title,
        icon,
        action,
        onDismiss,
        dismissLabel,
        live = DEFAULT_LIVE[tone],
        className,
    } = props;
    const { t } = useTranslation();

    return (
        <div
            role={ROLE[live]}
            data-tone={tone}
            className={classNames(
                styles.alert,
                styles[tone],
                layout === 'banner' && styles.banner,
                size === 'small' && styles.small,
                align === 'center' && styles.center,
                className
            )}>
            {icon != null && (
                <span className={styles.icon} aria-hidden="true">
                    {icon}
                </span>
            )}
            <div className={styles.body}>
                {title != null && <p className={styles.title}>{title}</p>}
                {children}
            </div>
            {action != null && <div className={styles.action}>{action}</div>}
            {onDismiss && (
                <button
                    type="button"
                    className={styles.dismiss}
                    aria-label={dismissLabel ?? t('atoms.alert.dismiss')}
                    onClick={onDismiss}>
                    <SvgIcon icon={CloseLine} size="small" color="currentColor" />
                </button>
            )}
        </div>
    );
}

export default Alert;
